import { useEffect, useRef, useState } from 'react'
import { type Dispatch, type SetStateAction } from 'react'
import LogTool from '../../../logger/logTools'
import { type FeedbackForm } from '../../Item/utils/types'
import { type SelectOption } from '../../../utils/types'
import { useCreateFeedbackForm } from './useFormEditorMutations'
import { useFeedbackForms } from '../../Item/hooks/useArticleQueries'

const log = new LogTool({ context: 'useFeedbackFormHandler', enable: true, logLevel: 'warn' })

type FormState = {
  state: any
  setState: Dispatch<SetStateAction<any>>
}

/**
 * Custom hook that handles all feedback form related logic for Sharepoint Drawers.
 * This hook manages:
 * - Loading initial feedback forms from the API
 * - Storing feedback forms in a ref
 * - Managing feedback form options for select inputs
 * - Managing form editor input state for FormEditorDrawer
 * - Creating new feedback forms and updating all related state
 *
 * @param formState - Form state object with state and setState (used to set selected feedback form)
 * @returns Object containing feedbackFormOptions, formEditorInput state, and handleAddFormOption function
 */
export function useFeedbackFormHandler(
  formState: FormState
): {
  feedbackFormOptions: SelectOption[]
  formEditorInput: any
  setFormEditorInput: Dispatch<SetStateAction<any>>
  handleAddFormOption: (feedbackForm: any) => Promise<'SUCCESS' | 'ERROR'>
  getFeedbackForm: (self: string) => FeedbackForm | undefined
} {
  const feedbackFormStore = useRef<FeedbackForm[]>([])
  const [feedbackFormOptions, setFeedbackFormOptions] = useState<SelectOption[]>([])
  const [formEditorInput, setFormEditorInput] = useState<any>({})
  const createFeedbackFormMutation = useCreateFeedbackForm()

  // Fetch feedback forms using React Query
  const { data: feedbackFormsData } = useFeedbackForms()

  // Initialize feedback forms from API data
  useEffect(() => {
    if (feedbackFormsData) {
      feedbackFormStore.current = [...feedbackFormStore.current, ...feedbackFormsData]
      setFeedbackFormOptions(
        feedbackFormStore.current.map(form => ({ value: form.self, label: form.formTitle }))
      )
    }
  }, [feedbackFormsData])

  const handleAddFormOption = async (feedbackForm: any): Promise<'SUCCESS' | 'ERROR'> => {
    log.info('Begin sending feedback form.')
    try {
      const response = await createFeedbackFormMutation.mutateAsync(feedbackForm)
      log.info('Success creating feedback form', response)
      const feedbackFormNew: FeedbackForm = response
      const newOption = { value: feedbackFormNew.self, label: feedbackFormNew.formTitle }

      // Add feedback form to formStore first
      feedbackFormStore.current = [...feedbackFormStore.current, feedbackFormNew]

      // Update options - check for duplicates to avoid adding the same form twice
      setFeedbackFormOptions(prev => {
        const existingValues = new Set(prev.map(opt => opt.value))
        if (existingValues.has(newOption.value)) {
          // Already exists, just return prev
          return prev
        }
        return [...prev, newOption]
      })

      // Set selected feedback form option
      formState.setState((prev: any) => {
        return {
          ...prev,
          feedbackForm: newOption,
        }
      })

      return 'SUCCESS'
    } catch (error) {
      log.error('Error while creating feedback form:', error)
      return 'ERROR'
    }
  }

  const getFeedbackForm = (self: string): FeedbackForm | undefined => {
    return feedbackFormStore.current.find((form: FeedbackForm) => form.self === self)
  }

  return {
    feedbackFormOptions,
    formEditorInput,
    setFormEditorInput,
    handleAddFormOption,
    getFeedbackForm,
  }
}
